/**
 * PostHog Analytics Service
 *
 * Provides unified analytics tracking for web and native platforms.
 * Handles user identification, event capture, and session management.
 *
 * Features:
 * - Platform-agnostic API (web uses posthog-js, native uses Capacitor plugin)
 * - Session replay and heatmaps (web only, built into PostHog)
 * - Autocapture for automatic click tracking
 * - User identification with properties
 *
 * @see https://posthog.com/docs/libraries/react
 * @see https://capawesome.io/plugins/posthog/
 */

import posthog from 'posthog-js';
import { Capacitor } from '@capacitor/core';
import { Posthog } from '@capawesome/capacitor-posthog';
import { scrubDigestIssueIds, scrubIssueIdProperties } from './scrubIssueIds';

const POSTHOG_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';
const POSTHOG_ENABLED = import.meta.env.VITE_POSTHOG_ENABLED !== 'false';

let isInitialized = false;

/** Stored init promise to support `waitForAnalytics()` and guard against double-init. */
let initPromise: Promise<void> | null = null;

/**
 * User properties for identification
 */
export interface UserProperties {
  email?: string;
  username?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  createdAt?: string;
  [key: string]: unknown;
}

/**
 * Minimal user shape required for analytics identification.
 * Intentionally decoupled from AuthUser to avoid circular dependencies with Redux slices.
 */
export interface IdentifiableUser {
  id: number;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
}

/**
 * Initialize PostHog analytics.
 * Should be called once at app startup.
 * Returns the stored promise on subsequent calls to guard against double-init.
 */
export const initAnalytics = (): Promise<void> => {
  // Return existing promise if already started (guards against double-init)
  if (initPromise) {
    return initPromise;
  }

  initPromise = (async () => {
    if (!POSTHOG_ENABLED) {
      console.log('[PostHog] Analytics disabled via environment');
      return;
    }

    if (!POSTHOG_KEY) {
      console.warn('[PostHog] Missing VITE_PUBLIC_POSTHOG_KEY - analytics will not be tracked');
      return;
    }

    if (isInitialized) {
      console.log('[PostHog] Already initialized');
      return;
    }

    try {
      if (Capacitor.isNativePlatform()) {
        // Native iOS/Android - use Capacitor plugin
        await Posthog.setup({
          apiKey: POSTHOG_KEY,
          host: POSTHOG_HOST,
        });
        console.log('[PostHog] Native SDK initialized');
      } else {
        // Web - use posthog-js
        posthog.init(POSTHOG_KEY, {
          api_host: POSTHOG_HOST,
          capture_pageview: false, // We handle this manually with TanStack Router
          capture_pageleave: true,
          autocapture: true, // Automatically capture clicks for heatmaps
          persistence: 'localStorage',
          // Templates `/dashboard/issues/<id>` to `/dashboard/issues/$id` in EVERY
          // event property posthog-js sends (its own `$pathname`, `$pageleave`,
          // autocapture URLs, `$set_once` ...), not just the router's PAGE_VIEW.
          before_send: scrubDigestIssueIds,
          session_recording: {
            maskAllInputs: false, // Set to true for stricter privacy
            maskTextSelector: '[data-mask]', // Mask specific elements
          },
          loaded: (posthogInstance) => {
            if (import.meta.env.DEV) {
              // Expose to window for debugging in development
              (window as unknown as { posthog: typeof posthog }).posthog = posthogInstance;
            }
          },
        });
        console.log('[PostHog] Web SDK initialized');
      }

      isInitialized = true;
    } catch (error) {
      console.error('[PostHog] Initialization failed:', error);
    }
  })();

  return initPromise;
};

/**
 * Unified analytics interface for both web and native platforms.
 */
export const analytics = {
  /**
   * Identify a user with their ID and properties.
   * Call this after user login/signup.
   *
   * @param userId - The unique user ID (typically user.id from backend)
   * @param properties - Optional user properties (email, name, etc.)
   */
  identify: (userId: string | number, properties?: UserProperties): void => {
    if (!isInitialized || !POSTHOG_ENABLED) return;

    const distinctId = String(userId);

    try {
      if (Capacitor.isNativePlatform()) {
        Posthog.identify({
          distinctId,
          userProperties: properties,
        });
      } else {
        posthog.identify(distinctId, properties);
      }
      console.log('[PostHog] User identified:', distinctId);
    } catch (error) {
      console.error('[PostHog] Identify failed:', error);
    }
  },

  /**
   * Capture an analytics event.
   *
   * @param event - The event name (e.g., 'restaurant_rated')
   * @param properties - Optional event properties
   */
  capture: (event: string, properties?: Record<string, unknown>): void => {
    if (!isInitialized || !POSTHOG_ENABLED) {
      if (import.meta.env.DEV) {
        console.log('[PostHog] Event (not sent):', event, properties);
      }
      return;
    }

    try {
      if (Capacitor.isNativePlatform()) {
        // The native SDK has no `before_send`, so the same scrub is applied here.
        Posthog.capture({ event, properties: properties && scrubIssueIdProperties(properties) });
      } else {
        posthog.capture(event, properties);
      }

      if (import.meta.env.DEV) {
        console.log('[PostHog] Event captured:', event, properties);
      }
    } catch (error) {
      console.error('[PostHog] Capture failed:', event, error);
    }
  },

  /**
   * Reset the user identity.
   * Call this on logout to clear the user session.
   */
  reset: (): void => {
    if (!isInitialized || !POSTHOG_ENABLED) return;

    try {
      if (Capacitor.isNativePlatform()) {
        Posthog.reset();
      } else {
        posthog.reset();
      }
      console.log('[PostHog] User session reset');
    } catch (error) {
      console.error('[PostHog] Reset failed:', error);
    }
  },

  /**
   * Set properties on the current user without changing identity.
   *
   * @param properties - User properties to set
   */
  setUserProperties: (properties: UserProperties): void => {
    if (!isInitialized || !POSTHOG_ENABLED) return;

    try {
      if (Capacitor.isNativePlatform()) {
        // Native plugin doesn't have setPersonProperties, use identify with existing ID
        console.warn('[PostHog] setUserProperties not supported on native, use identify instead');
      } else {
        posthog.setPersonProperties(properties);
      }
    } catch (error) {
      console.error('[PostHog] Set user properties failed:', error);
    }
  },

  /**
   * Register super properties that are sent with every event.
   *
   * @param properties - Properties to register globally
   */
  register: (properties: Record<string, unknown>): void => {
    if (!isInitialized || !POSTHOG_ENABLED) return;

    try {
      if (Capacitor.isNativePlatform()) {
        // Native plugin doesn't support super properties directly
        console.warn('[PostHog] Super properties not supported on native');
      } else {
        posthog.register(properties);
      }
    } catch (error) {
      console.error('[PostHog] Register failed:', error);
    }
  },

  /**
   * Check if analytics is initialized and enabled.
   */
  get isReady(): boolean {
    return isInitialized && POSTHOG_ENABLED;
  },

  /**
   * Check if analytics is enabled via environment.
   */
  get isEnabled(): boolean {
    return POSTHOG_ENABLED;
  },
};

/**
 * Wait for analytics initialization to complete.
 * Returns the stored init promise, or resolves immediately if init hasn't been called.
 * Use this instead of polling `analytics.isReady`.
 */
export const waitForAnalytics = (): Promise<void> => {
  return initPromise ?? Promise.resolve();
};

/**
 * Identify a user in PostHog with the standard property bag.
 * Centralizes the identify call so all call sites use a consistent set of properties.
 *
 * @param user - An object with at least the fields needed for identification
 */
export const identifyUser = (user: IdentifiableUser): void => {
  analytics.identify(user.id.toString(), {
    email: user.email,
    name: user.name,
    firstName: user.firstName,
    lastName: user.lastName,
  });
};

export default analytics;
